/** Versioned display results. Producers, not browsers or storage adapters, calculate metrics. */
export const METRICS_CONTRACT_VERSION = 2 as const;
export const METRIC_SOURCES = ["fake", "metrics-3.1", "native", "hubspot"] as const;
export type MetricSource = (typeof METRIC_SOURCES)[number];
export const METRIC_SOURCE_LABELS: Record<MetricSource, string> = {
  fake: "Illustrative data",
  "metrics-3.1": "Metrics 3.1",
  native: "Native metrics",
  hubspot: "HubSpot",
};
export const METRIC_REASON_CODES = [
  "missing_goal",
  "stale_availability",
  "not_enough_clients",
  "short_history",
  "no_source",
  "incomplete_source",
  "zero_denominator",
  "source_failed",
  "incompatible_definition",
] as const;
export type MetricReasonCode = (typeof METRIC_REASON_CODES)[number];
export interface MetricReason {
  code: MetricReasonCode;
  detail?: string;
}
const REASON_LABELS: Record<MetricReasonCode, string> = {
  missing_goal: "Goal not recorded",
  stale_availability: "Availability needs updating",
  not_enough_clients: "Not enough clients",
  short_history: "Not enough history",
  no_source: "No source data",
  incomplete_source: "Source data is incomplete",
  zero_denominator: "No eligible population",
  source_failed: "Source unavailable",
  incompatible_definition: "Different metric definition",
};

export function metricReasonText(reason: MetricReason | null): string | null {
  return reason ? (reason.detail ?? REASON_LABELS[reason.code]) : null;
}

export const WEEKLY_METRIC_PERIODS = [
  "last_four_weeks",
  "last_week",
  "prior_week",
  "year_to_date",
  "all_time",
] as const;
export const MONTHLY_METRIC_PERIODS = [
  "current_month",
  "prior_month",
  "two_months_ago",
  "rolling_three_months",
  "since_start",
] as const;
export type MetricPeriodId =
  | (typeof WEEKLY_METRIC_PERIODS)[number]
  | (typeof MONTHLY_METRIC_PERIODS)[number];
export interface MetricPeriod {
  id: MetricPeriodId;
  label: string;
  start: string;
  end: string;
  value: number | null;
  partial: boolean;
  source: MetricSource;
  definitionVersion: string;
  reason: MetricReason | null;
}
export const METRIC_NAMES = [
  "clinicalHours",
  "billableHours",
  "billableUtilization",
  "forwardLoad",
  "retention",
  "recurringBookings",
  "cancellationRate",
  "newReferrals",
  "ownedReferrals",
  "visitCadence",
] as const;
export type MetricName = (typeof METRIC_NAMES)[number];
export type MetricChip =
  | "Below"
  | "Watch"
  | "Meets"
  | "Above"
  | "No target"
  | "Too early";
export type MetricUnit = "hours" | "percent" | "visits" | "referrals";
export const METRIC_DENOMINATORS = [
  "absolute",
  "per_day",
  "per_week",
  "per_month",
] as const;
export type MetricDenominator = (typeof METRIC_DENOMINATORS)[number];
/** Client-facing labels; clients render from the enum, never from free text. */
export const METRIC_DENOMINATOR_LABELS: Record<
  MetricDenominator,
  { long: string; short: string }
> = {
  absolute: { long: "", short: "" },
  per_day: { long: "per day", short: "/day" },
  per_week: { long: "per week", short: "/wk" },
  per_month: { long: "per month", short: "/mo" },
};
export type MetricTarget =
  | { kind: "absolute"; value: number; label: string }
  /** At least one bound is non-null. */
  | { kind: "range"; min: number | null; max: number | null; label: string };
/**
 * What a graded target was measured against, where the producer states it.
 * Only billable hours carries one: `contract_minimum` is the RD's own
 * Functional form minimum, and `company_floor` means no minimum is on file and
 * the value was graded against the company floor instead, which the client
 * flags with a link to set the minimum. Clients read the basis; they never
 * infer it from the target label or value.
 */
export const METRIC_TARGET_BASES = ["contract_minimum", "company_floor"] as const;
export type MetricTargetBasis = (typeof METRIC_TARGET_BASES)[number];
export interface MetricBand {
  min: number | null;
  max: number | null;
}
export interface MetricEnvelope {
  /** `null` for missing, never a synthesized 0. */
  value: number | null;
  /** Quantity unit; hrs/wk is `hours` + `per_week`. */
  unit: MetricUnit;
  per: MetricDenominator;
  /** When `tooEarly` is true the chip is "Too early". */
  chip: MetricChip;
  tooEarly: boolean;
  /** Why `value` is null, when it is. */
  reason: MetricReason | null;
  target: MetricTarget | null;
  /**
   * Set only at read time, only on a graded billable-hours envelope, and only
   * alongside a non-null `target`. Absent everywhere else (NO-667).
   */
  targetBasis?: MetricTargetBasis;
  /** Explicit good-zone bounds; never inferred from labels or client tolerances. */
  good: MetricBand | null;
  percentOfTarget: number | null;
  /** Amber band the zoned goal bar draws. */
  watch: MetricBand | null;
  fleet: {
    median: number | null;
    p75: number | null;
    label: string;
    rdCount: number;
  } | null;
  /** Deterministic one-line read for the tile. */
  sentence: string | null;
  window: { start: string; end: string; label: string };
  source: MetricSource;
  definitionVersion: string;
  dataThrough: string;
  definition: string;
  /** Present on detail only. */
  periods?: MetricPeriod[];
}

export type TenureBand = "0-90" | "90+";
export interface RdTenure {
  /** ISO calendar date the RD's tenure is counted from. */
  startDate: string;
  /** Whole days from `startDate` to the last data day. */
  daysAsOf: number;
  band: TenureBand;
  source: MetricSource;
}

export type FunnelStepId = "s1" | "s2" | "s3" | "s4_5" | "s6plus" | "s12plus";
/** Percent range of first visits a step is expected to keep. */
export interface FunnelReachRange {
  lo: number;
  hi: number;
}
/** The target for reaching a step; the gap before the step renders it. */
export type FunnelTarget =
  | { kind: "maxDropOff"; value: number }
  | { kind: "reach"; value: number }
  | { kind: "medicareCapExcluded" }
  | { kind: "none" };
export type FunnelStepStatus = Extract<
  MetricChip,
  "Meets" | "Watch" | "Below" | "No target"
>;
export interface FunnelStep {
  id: FunnelStepId;
  label: string;
  patients: number;
  /** s3 only: patients whose Medicare 3-session cap ends here; excluded from later steps. */
  medicareCapped?: number;
}
export interface FunnelBandStep {
  target: FunnelTarget;
  /** Fixture display state supplied by the provider, never graded on the client. */
  status: FunnelStepStatus;
  shouldReach?: FunnelReachRange;
}
export interface FunnelBandTargets {
  steps: Record<FunnelStepId, FunnelBandStep>;
}
export interface SessionFunnel {
  cohort: {
    firstVisitStart: string;
    firstVisitEnd: string;
    patients: number;
    followedDays: number;
  };
  /** Exactly six steps in `FunnelStepId` order. */
  steps: FunnelStep[];
  targets: Record<TenureBand, FunnelBandTargets>;
  source: MetricSource;
}

export interface RdMetrics {
  version: typeof METRICS_CONTRACT_VERSION;
  rdId: string;
  dataThrough: string;
  metrics: Record<MetricName, MetricEnvelope>;
  /** Detail blocks; never present on roster rows. */
  funnel?: SessionFunnel;
  tenure?: RdTenure;
}
export type CompactMetricEnvelope = Omit<MetricEnvelope, "periods">;
export interface CompactRdMetrics extends Omit<
  RdMetrics,
  "metrics" | "funnel" | "tenure"
> {
  metrics: Record<MetricName, CompactMetricEnvelope>;
}

/**
 * The RD exists and the read succeeded, but the latest published batch carries
 * no record for them — a newly onboarded RD between weekly publications.
 * Distinct from `503 Metrics unavailable`, which stays reserved for a failed
 * read and for the case where no publication exists at all, and distinct from a
 * null metric value, which always carries its own reason code.
 *
 * Additive: a published response keeps its exact `RdMetrics` shape, so the
 * contract version does not move.
 */
export interface RdMetricsNotPublished {
  version: typeof METRICS_CONTRACT_VERSION;
  rdId: string;
  notPublished: true;
}
export type RdMetricsResponse = RdMetrics | RdMetricsNotPublished;

export function isRdMetricsNotPublished(
  response: RdMetricsResponse
): response is RdMetricsNotPublished {
  return (response as RdMetricsNotPublished).notPublished === true;
}

export function rdMetricsNotPublished(rdId: string): RdMetricsNotPublished {
  return { version: METRICS_CONTRACT_VERSION, rdId, notPublished: true };
}
